import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  company?: string;
}

interface AuthContextType {
  user: User | null | undefined;
  login: (email: string, password: string) => Promise<void>;
  register: (data: { email: string; password: string; name: string; company?: string; phone?: string }) => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: undefined,
  login: async () => {},
  register: async () => {},
  logout: async () => {},
  loading: true,
});

function formatApiError(detail: any): string {
  if (detail == null) return 'Something went wrong.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e: any) => e?.msg || JSON.stringify(e)).filter(Boolean).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('maluz_access_token');
  if (token) return { Authorization: `Bearer ${token}` };
  return {};
}

// Set up axios interceptor to add auth header to all requests
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('maluz_access_token');
  if (token) {
    (config.headers as any).Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auto-refresh token on 401
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401 && !error.config._retry) {
      error.config._retry = true;
      const refreshToken = localStorage.getItem('maluz_refresh_token');
      if (refreshToken) {
        try {
          const { data } = await axios.post(`${API}/api/auth/refresh`, { refresh_token: refreshToken }, { headers: { Authorization: `Bearer ${refreshToken}` } });
          localStorage.setItem('maluz_access_token', data.access_token);
          error.config.headers.Authorization = `Bearer ${data.access_token}`;
          return axios(error.config);
        } catch { /* refresh failed, let it fall through */ }
      }
    }
    return Promise.reject(error);
  }
);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('maluz_access_token');
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    axios.get(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setUser(res.data))
      .catch(() => {
        localStorage.removeItem('maluz_access_token');
        localStorage.removeItem('maluz_refresh_token');
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    try {
      const { data } = await axios.post(`${API}/api/auth/login`, { email, password });
      localStorage.setItem('maluz_access_token', data.access_token);
      localStorage.setItem('maluz_refresh_token', data.refresh_token);
      setUser({ id: data.id, email: data.email, name: data.name, role: data.role, company: data.company });
    } catch (e: any) {
      throw new Error(formatApiError(e.response?.data?.detail) || e.message);
    }
  }, []);

  const register = useCallback(async (formData: any) => {
    try {
      const { data } = await axios.post(`${API}/api/auth/register`, formData);
      localStorage.setItem('maluz_access_token', data.access_token);
      localStorage.setItem('maluz_refresh_token', data.refresh_token);
      setUser({ id: data.id, email: data.email, name: data.name, role: data.role, company: data.company });
    } catch (e: any) {
      throw new Error(formatApiError(e.response?.data?.detail) || e.message);
    }
  }, []);

  const logout = useCallback(async () => {
    try { await axios.post(`${API}/api/auth/logout`); } catch {}
    localStorage.removeItem('maluz_access_token');
    localStorage.removeItem('maluz_refresh_token');
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
